import { useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Projects from "./Projects";

// Icons / images
import codeI from "../icons/code.gif";
import stack from "../icons/stack.png";
import Azentio1 from "../icons/azentio_logo.jpg";
import Elswedy from "../icons/elswedy.webp";

import CBE from "../icons/CBE.png";
import Vision from "../icons/VISINBANK.jpg";
import Rammis from "../icons/RAMMIS.webp";
import ElsewedyIcon from "../icons/elswedy.webp";
import Amana from "../icons/AMANA.jpg";
import Logs from "../icons/elswedy.webp";
import ALBARAKA from "../icons/ALBARAKA.png";
import NB_bank from "../icons/NB_bank.jpg";
import IDB from "../icons/idb.jpg";

/* ---------- Helpers: date → months → label ---------- */
const parseYYYYMM = (s) => {
  // expects 'YYYY-MM' or 'present'
  if (!s || s?.toLowerCase?.() === "present") return null;
  const [y, m] = s.split("-").map(Number);
  return new Date(y, (m || 1) - 1, 1);
};

const monthsDiff = (fromStr, toStr, now = new Date()) => {
  const from = parseYYYYMM(fromStr);
  const to = parseYYYYMM(toStr) || now;
  if (!from) return 0;
  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let total = years * 12 + months;
  return Math.max(0, total);
};

const durationLabel = (months) => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  const yPart = y > 0 ? `${y} yr${y > 1 ? "s" : ""}` : "";
  const mPart = m > 0 ? `${m} mo${m > 1 ? "s" : ""}` : "";
  return [yPart, mPart].filter(Boolean).join(" ");
};

/* ---------- Data (enhanced Azentio section) ---------- */
const jobs = [
  {
    company: "Azentio Software",
    subtitle: "(World's #1 Islamic Core Banking platform - iMAL)",
    logo: Azentio1,
    dates: { from: "2023-06", to: "present", label: "Jun 2023 – Present" },
    summary:
      "Software Engineer & Technical Consultant specializing in iMAL Islamic banking systems and large-scale file-based integrations across telecom, government, and financial entities.",
    stack: [
      "Java",
      "Spring MVC",
      "Oracle DB",
      "SQL Server",
      "iMAL",
      "PL/SQL",
      "DFC/DFS",
      "REST",
      "Banking Integrations",
      "File-based Automation",
      "Reconciliation",
      "Reporting Design",
      "Procedure Optimization",
      "Client Training",
      "Documentation",
      "Digital Banking",
      "APIs",
      "Scheduler Automation",
      "ETL Control Tables",
      "ISO 20022 (pacs/camt)",
    ],
    badge: { text: "Banking Projects", variant: "secondary" },
    projects: [
      { title: "Vision Bank (UAE)", blurb: "Enhanced file upload automation in iMAL.", icon: Vision },
      { title: "CBE (Ethiopia)", blurb: "REST-based integration with telecom and government entities.", icon: CBE },
      { title: "Rammis Bank", blurb: "Transaction alerts via SMS/email using Oracle DB.", icon: Rammis },
      { title: "Batch Salary Module (Amana Bank)", blurb: "Streamlined payroll upload & reconciliation.", icon: Amana },
      { title: "IMAL Integration (Albaraka Bank)", blurb: "Implemented and maintained iMAL integrations.", icon: ALBARAKA },
      { title: "National Bonds (UAE)", blurb: "Maintained iMAL modules and automations.", icon: NB_bank },
      { title: "International Development (UAE)", blurb: "Maintained iMAL integrations.", icon: IDB },
    ],
    projectsProps: { showShapes: true, showArrows: true, autoScroll: true, speed: 0.9, iconSize: 90 },

    highlights: [
      {
        label: "Core Integrations",
        items: [
          "Safaricom H2H settlement & H2H STP transfers",
          "Telebirr & CBEBIIR file-based flows via DFC/DFS",
          "ECX integration (dual-row DR/CR model)",
          "Payroll integration",
          "GLTT & IFT automation for JV posting via P_CBE_INSERT_MULTI_JV",
        ],
      },
      {
        label: "Impact",
        items: [
          "Reduced manual posting by ~85% via automated JV pipelines",
          "Cut file turnaround time from hours → minutes using scheduler-based ingestion",
          "Improved reconciliation accuracy with idempotent batch keys & structured error logging",
          "Accelerated client delivery through reusable DFC templates",
          "Created complex reports using Jaspersoft",
          "Built reconciliation to fix false entries",
        ],
      },
      {
        label: "Responsibilities",
        items: [
          "Design PL/SQL procedures for direct processing (avoiding staging tables where possible)",
          "Build DFC export specs with dynamic header/footer validation",
          "Lead code reviews, create runbooks, and deliver client training sessions",
          "Collaborate with banking teams for GL/account mapping & reconciliation accuracy",
        ],
      },
    ],

    kpis: [
      { value: "12+", label: "projects" },
      { value: "4", label: "go-lives" },
      { value: "98%", label: "client CSAT" },
      { value: "85%", label: "automation efficiency" },
    ],

    innovation: [
      {
        label: "System Architecture",
        items: [
          "Standardized DFC/DFS structure for rapid rollout",
          "Job-chain schedulers via DBMS_SCHEDULER",
          "Audit trail & ETL control tables for traceability",
        ],
      },
      {
        label: "Knowledge & Mentorship",
        items: [
          "Internal guides for DFC best practices",
          "Mentored junior consultants on PL/SQL & troubleshooting",
          "UMG and runbook documentation for client handovers",
        ],
      },
      {
        label: "Future Focus",
        items: [
          "Integration Architect track: Digital Hub & ISO 20022",
          "Microservice-based REST gateways for DFC reuse",
          "Reconciliation dashboards (Power BI / APEX)",
        ],
      },
    ],
  },
  {
    company: "Elsewedy Electric",
    subtitle: "(Cables Section)",
    logo: Elswedy,
    dates: { from: "2022-07", to: "2023-07", label: "Jul 2022 – Jul 2023" },
    summary: "Built, maintained, and tested software to improve productivity and reduce cost.",
    stack: [
      "Java 6/8/17",
      "Spring Boot 3",
      "SQL Server",
      "Git",
      "Spring MVC",
      "Spring Data",
      "Spring Security",
      "JWT",
    ],
    badge: { text: "Cables Projects", variant: "secondary" },
    projects: [
      { title: "Elsewedy Web Cable Portal", blurb: "End-to-end portal with Spring + React.", icon: ElsewedyIcon },
      { title: "Logs Dashboard", blurb: "Visual analytics with React + Spring Boot.", icon: Logs },
      { title: "Performance Refactor", blurb: "Refactored and optimized cable workflow.", icon: Logs },
    ],
    projectsProps: { showShapes: false, showArrows: true, autoScroll: true, speed: 0.7, iconSize: 90 },
  },
  {
    company: "ValueInSoft",
    dates: { from: "2020-11", to: "2022-03", label: "Nov 2020 – Mar 2022" },
    summary: "Full-stack web app for small shops (multi-tenant, per-schema) on Spring Boot + React.",
    stack: [
      "React (DOM, Formik, Axios, Router, RB)",
      "Spring Boot (JPA, Security, JWT, JDBC)",
      "Heroku",
      "PostgreSQL (multi-schema)",
    ],
    badge: { text: "POS  Cloud Project", variant: "secondary" },
  },
  {
    company: "Car Workshop Management System",
    dates: { from: "2020-08", to: "2021-03", label: "Aug 2020 – Mar 2021" },
    summary:
      "Desktop app with POS, inventory, employees, clients, spare parts & finance — multi-branch MySQL; Swing UI.",
    stack: ["Java", "MySQL", "Swing", "OOP"],
    badge: { text: "Team Project", variant: "secondary" },
  },
  {
    company: "Heart Sounds Classification — Smart-tuned DL Model",
    dates: { from: "2019-10", to: "2020-08", label: "Oct 2019 – Aug 2020" },
    summary:
      "Graduation project (A+) with BUE: adaptive training on small datasets; Python ML stack.",
    stack: [
      "Python",
      "librosa",
      "numpy",
      "sklearn",
      "matplotlib",
      "pydub",
      "scipy",
      "tensorflow",
      "pandas",
      "keras",
    ],
    badge: { text: "Graduation Project", variant: "secondary" },
  },
];

/* Precompute max months to scale bars + total career */
const useTimelineMetrics = () => {
  return useMemo(() => {
    const monthsPerJob = jobs.map((j) => monthsDiff(j.dates?.from, j.dates?.to));
    const maxMonths = Math.max(...monthsPerJob, 1);
    const earliestFrom = jobs.map((j) => j.dates?.from).filter(Boolean).sort()[0];
    const totalCareerMonths = monthsDiff(earliestFrom, "present");
    const totalCareerLabel = durationLabel(totalCareerMonths);
    return { monthsPerJob, maxMonths, totalCareerMonths, totalCareerLabel };
  }, []);
};

/* Small animations */
const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(17,113,239,.35); }
  50% { transform: scale(1.08); box-shadow: 0 0 0 8px rgba(17,113,239,.08); }
`;
const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

const ExperienceEnhanced = () => {
  const { maxMonths, totalCareerMonths, totalCareerLabel } = useTimelineMetrics();
  const [expandedCompany, setExpandedCompany] = useState("Azentio Software");

  return (
    <Section role="region" aria-label="Experience">
      <div className="container">
        {/* Page header */}
        <header className="page-header">
          <h1 className="title">
            <img src={codeI} alt="Experience icon" width={60} height={60} loading="lazy" />
            <span>Experience</span>
          </h1>
          <p className="lede">
            A snapshot of my work across banking, enterprise software, and research projects.
          </p>
          {totalCareerLabel && (
            <div className="meta-row" aria-label="Total experience">
              <span className="meta-badge">
                Total Experience: <strong>{totalCareerLabel}</strong>
                <span>·</span>
                Total Working Months: <strong>{totalCareerMonths}</strong>
              </span>
            </div>
          )}
        </header>

        {/* Timeline of roles */}
        <div className="timeline">
          {jobs.map((job, idx) => {
            const m = monthsDiff(job.dates?.from, job.dates?.to);
            const label = m ? durationLabel(m) : "";
            const isExpanded = expandedCompany === job.company;
            const widthPct = Math.max(10, Math.round((m / maxMonths) * 100));

            return (
              <article key={job.company} className="job">
                {/* timeline dot + connector */}
                <div className="dot" aria-hidden="true" />
                {idx !== jobs.length - 1 && <div className="line" aria-hidden="true" />}

                {/* job card */}
                <div className="card">
                  <Row className="gx-4 gy-3">
                    <Col xs={12} md={7}>
                      <div className="company-row">
                        {job.logo && (
                          <img
                            src={job.logo}
                            alt={`${job.company} logo`}
                            width={56}
                            height={56}
                            loading="lazy"
                            className="logo"
                          />
                        )}
                        <div>
                          <h3 className="company">
                            <strong>{job.company}</strong>{" "}
                            {job.subtitle && <span className="sub font_sm">{job.subtitle}</span>}
                          </h3>

                          {/* Dates + Duration */}
                          {(job.dates?.label || label) && (
                            <div className="date-row">
                              {job.dates?.label && (
                                <Badge bg="light" className="dates">
                                  <time dateTime={job.dates.from || ""}>{job.dates.label}</time>
                                </Badge>
                              )}
                              {label && <span className="duration">• {label}</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      {job.summary && <p className="summary">{job.summary}</p>}

                      {/* Duration bar (visual) */}
                      <div className="duration-bar-wrap" aria-label="Role duration">
                        <div className="duration-bar" style={{ width: `${widthPct}%` }} />
                      </div>

                      {/* Highlights */}
                      {job.highlights && (
                        <div className="highlights">
                          {job.highlights.map((group) => (
                            <div key={group.label} className="highlight-group">
                              <div className="highlight-title">{group.label}</div>
                              <ul className="highlight-list">
                                {group.items.map((it, i) => (
                                  <li key={i}>{it}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}

                      {job.kpis && (
                        <div className="kpi-row" aria-label="Quick KPIs">
                          {job.kpis.map((k) => (
                            <div key={k.label} className="kpi-chip" title={k.label}>
                              <span className="kpi-val">{k.value}</span>
                              <span className="kpi-label">{k.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Col>

                    <Col xs={12} md={5}>
                      {job.stack?.length > 0 && (
                        <>
                          <div className="stack-title">
                            <img src={stack} alt="Tech stack" width={24} height={24} loading="lazy" />
                            <span>Stack</span>
                          </div>
                          <div className="chips">
                            {job.stack.map((s) => (
                              <span key={s} className="chip">
                                {s}
                              </span>
                            ))}
                          </div>
                        </>
                      )}

                      {job.badge?.text && (
                        <Badge style={{ fontSize: 12 }} bg={job.badge.variant || "secondary"}>
                          {job.badge.text}
                        </Badge>
                      )}

                      <br />
                      {(job.projects?.length > 0 || job.highlights) && (
                        <button
                          className="toggle-btn"
                          aria-expanded={isExpanded}
                          onClick={() => setExpandedCompany(isExpanded ? null : job.company)}
                        >
                          {isExpanded ? "Hide details" : "Show details"}
                        </button>
                      )}
                    </Col>
                  </Row>

                  {(job.projects?.length > 0 || job.highlights) && isExpanded && (
                    <div className="projects-wrap">
                      {job.projects?.length > 0 && (
                        <Projects
                          items={job.projects}
                          heading="🏆 Highlight Projects"
                          {...(job.projectsProps || {})}
                        />
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

export default ExperienceEnhanced;

/* ===== styled-components (presentation only) ===== */
const Section = styled.section`
  /* Theme tokens */
  --ink: #0a2a43;
  --ink-dim: #244e7a;
  --primary: #0a5fd8;
  --primary-2: #1171ef;
  --glass: rgba(255,255,255,.9);
  --chip-bg: rgba(10,95,216,.08);
  --chip-bd: rgba(10,95,216,.25);
  --line-fade: rgba(10,95,216,.08);

  @media (prefers-color-scheme: dark) {
    --ink: #eaf1fa;
    --ink-dim: #c9d8ef;
    --glass: rgba(16,28,45,.6);
    --chip-bg: rgba(17,113,239,.18);
    --chip-bd: rgba(17,113,239,.45);
    --line-fade: rgba(17,113,239,.18);
  }

  padding: 64px 0 40px;
  background: transparent;
  color: var(--ink);

  .container { width: min(1100px, 92%); margin: 0 auto; }

  /* header */
  .page-header { text-align: center; margin-bottom: 22px; }
  .title {
    display: inline-flex; align-items: center; gap: 12px;
    font-weight: 800; font-size: clamp(28px, 4vw, 40px); margin: 0;
  }
  .title img { filter: drop-shadow(0 8px 14px rgba(0,0,0,.18)); }
  .lede { margin: 6px auto 0; max-width: 720px; opacity: .9; color: var(--ink-dim); }

  .meta-row { margin-top: 10px; }
  .meta-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 999px;
    background: var(--chip-bg); border: 1px solid var(--chip-bd);
    font-weight: 700;
  }

  /* timeline layout */
  .timeline { position: relative; margin-top: 18px; }
  .job { position: relative; padding-left: 34px; margin-bottom: 28px; }
  .dot {
    position: absolute; left: 8px; top: 10px; width: 14px; height: 14px;
    background: var(--primary); border-radius: 50%;
    animation: ${pulse} 2.4s ease-in-out infinite;
  }
  .line {
    position: absolute; left: 14px; top: 28px; bottom: -4px; width: 2px;
    background: linear-gradient(var(--primary), var(--line-fade));
  }

  .font_sm{
    font-size: 13px;
  }
  /* job card */
  .card {
    background: var(--glass);
    border: 1px solid rgba(10,95,216,.15);
    border-radius: 18px;
    padding: 18px 18px 10px;
    box-shadow: 0 8px 24px rgba(3,20,44,.1);
    transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
    backdrop-filter: saturate(140%) blur(6px);
  }
  .card:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(3,20,44,.16); border-color: rgba(10,95,216,.35); }

  .company-row { display: flex; align-items: center; gap: 12px; }
  .company { margin: 0; font-size: 18px; }
  .sub { opacity: .9; font-weight: 500; color: var(--ink-dim); }
  .logo { border-radius: 10px; }

  .date-row {
    display: flex; align-items: center; gap: 10px; margin-top: 4px;
    flex-wrap: wrap;
  }
  .dates { color: var(--ink) !important; }
  .duration { font-weight: 700; color: var(--primary); }

  .summary { margin: 10px 0 0; opacity: .95; line-height: 1.6; color: var(--ink-dim); }

  .stack-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 700; margin-bottom: 6px; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
  .chip {
    background: var(--chip-bg);
    border: 1px solid var(--chip-bd);
    color: var(--ink);
    border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 600;
  }

  .projects-wrap { margin-top: 10px; }

  /* Duration bar: animated gradient */
  .duration-bar-wrap {
    margin-top: 10px; height: 8px; width: 100%;
    background: var(--chip-bg); border-radius: 999px; overflow: hidden;
  }
  .duration-bar {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--primary), var(--primary-2), var(--primary));
    background-size: 200% 100%;
    animation: ${shimmer} 6s linear infinite;
    transition: width .4s ease;
  }

  /* NEW: highlights + KPI styling */
  .highlights { margin-top: 12px; display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
  .highlight-group { background: rgba(10,95,216,.06); border: 1px dashed var(--chip-bd); border-radius: 12px; padding: 10px; }
  .highlight-title { font-weight: 700; margin-bottom: 6px; color: var(--ink); }
  .highlight-list { margin: 0; padding-left: 18px; color: var(--ink-dim); }

  .kpi-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
  .kpi-chip { display: inline-flex; align-items: baseline; gap: 6px; background: var(--chip-bg); border: 1px solid var(--chip-bd); padding: 6px 10px; border-radius: 999px; }
  .kpi-val { font-weight: 800; }
  .kpi-label { font-size: 12px; color: var(--ink-dim); text-transform: uppercase; letter-spacing: .4px; }

  .toggle-btn { margin-top: 12px; padding: 6px 10px; border-radius: 10px; border: 1px solid var(--chip-bd); background: var(--glass); font-weight: 700; cursor: pointer; }
  .toggle-btn:hover { border-color: var(--primary); }

  /* responsive */
  @media (max-width: 768px) {
    .job { padding-left: 28px; }
    .dot { left: 4px; }
    .line { left: 10px; }
  }
`;